import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { DetailsInfoItem } from './components/DetailsInfoItem/DetailsInfoItem';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import {
    getMedianPrice,
    getPriceRangeHigh,
    getPriceRangeLow,
    getTotalSales,
} from '@/redux/modules/priceResults/priceResults.selectors';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { useAppSelector } from '@/redux/hooks';
import { useRequireLogin } from '@/components/ShouldLogIn/hooks/useRequireLogin';
import FormattedCurrencyOrPreferred from '@/components/Formatted/FormattedCurrencyOrPreferred';

type Props = {
    className?: string;
};

export const PriceResultsItemDetails = ({ className }: Props) => {
    const verifyLogin = useRequireLogin();
    const { formatMessage, formatNumber } = useIntl();
    const totalSales = useAppSelector(getTotalSales);
    const medianPrice = useAppSelector(getMedianPrice);
    const priceRangeHigh = useAppSelector(getPriceRangeHigh);
    const priceRangeLow = useAppSelector(getPriceRangeLow);
    const isUserFullyLoggedIn = useAppSelector(isUserLoggedIn);

    const onUnlockItemClick = () => {
        verifyLogin({
            requireLogin: true,
        });
    };

    const medianPriceNode =
        medianPrice > 0 ? (
            <FormattedCurrencyOrPreferred
                noFraction
                value={medianPrice}
            />
        ) : (
            <FormattedMessage id="priceResultsPage.unknownStat" />
        );

    const priceRangeNode =
        priceRangeLow > 0 && priceRangeHigh > 0
            ? formatMessage(
                  {
                      id: 'priceResultsPage.priceRangeLabel',
                  },
                  {
                      highPrice: (
                          <FormattedCurrencyOrPreferred
                              key="price-results-item-details-high-price"
                              noFraction
                              value={priceRangeHigh}
                          />
                      ),
                      lowPrice: (
                          <FormattedCurrencyOrPreferred
                              key="price-results-item-details-low-price"
                              noFraction
                              value={priceRangeLow}
                          />
                      ),
                  }
              )
            : formatMessage({ id: 'priceResultsPage.unknownStat' });

    return (
        <div
            className={cn(
                'flex items-start gap-sm smMax:scrollbar-hide smMax:gap-xs smMax:overflow-x-auto xsMax:px-sm',
                className
            )}
        >
            <DetailsInfoItem
                label={<FormattedMessage id="priceResultsPage.totalLabel" />}
                value={
                    <FormattedMessage
                        id="priceResultsPage.totalItemsSold"
                        values={{ totalItemsSold: formatNumber(totalSales) }}
                    />
                }
            />
            <DetailsInfoItem
                isLoggedIn={isUserFullyLoggedIn}
                label={<FormattedMessage id="priceResultsPage.medianLabel" />}
                onUnlockItemClick={onUnlockItemClick}
                requiresLogin
                unlockTestId="unlock-median-price"
                value={medianPriceNode}
            />
            <DetailsInfoItem
                isLoggedIn={isUserFullyLoggedIn}
                label={<FormattedMessage id="priceResultsPage.rangeLabel" />}
                onUnlockItemClick={onUnlockItemClick}
                requiresLogin
                unlockTestId="unlock-price-range"
                value={priceRangeNode}
            />
        </div>
    );
};
